/**
 * Secondary-video behavior for product cards (see snippets/product-card.liquid).
 *
 * This used to be two inline <script> blocks emitted once per product card (~3.5KB each, 31 copies on the homepage).
 * It now lives in a single cached asset, loaded with `defer` from layout/theme.liquid. Behavior is unchanged.
 */
(function () {
  if (window.__productCardVideoInit) return;
  window.__productCardVideoInit = true;

  /*
   * The secondary video is rendered with <source data-src> (see the `defer_src` option of snippets/media.liquid) so the
   * ~1MB 1080p file isn't downloaded for every card on page load. The first time a video is about to play, its sources
   * are attached here and the element reloaded. Later calls are no-ops.
   */
  function playSecondaryVideo(videoMedia) {
    var sources = videoMedia.querySelectorAll('source[data-src]');

    if (sources.length) {
      sources.forEach(function (source) {
        source.src = source.getAttribute('data-src');
        source.removeAttribute('data-src');
      });

      var element = videoMedia.querySelector('video');
      if (element) element.load();
    }

    if (typeof videoMedia.play === 'function') {
      videoMedia.play();
    }
  }

  /*
   * Desktop hover: the secondary media only crossfades in via CSS on hover (see .product-card__image--secondary).
   * When it's a video, hovering also needs to actually start playback (and pause it back on hover-out), which CSS
   * alone can't do. "pointerenter"/"pointerleave" don't bubble, so the capture phase is used to catch them from any
   * depth.
   */
  document.addEventListener('pointerenter', function (event) {
    var figure = event.target;
    if (!figure.classList || !figure.classList.contains('product-card__figure')) return;

    var video = figure.querySelector('video-media.product-card__image--secondary');
    if (video) {
      playSecondaryVideo(video);
    }
  }, true);

  document.addEventListener('pointerleave', function (event) {
    var figure = event.target;
    if (!figure.classList || !figure.classList.contains('product-card__figure')) return;

    var video = figure.querySelector('video-media.product-card__image--secondary');
    if (video && typeof video.pause === 'function') {
      video.pause();
    }
  }, true);

  /*
   * Mobile: there's no hover, so the secondary video is instead autoplayed once a card has sat in view for 3s.
   * IntersectionObserver (not a window "scroll" listener) drives this so it stays correct for cards inside
   * horizontally-scrolling carousels (e.g. the homepage product carousels) - it accounts for clipping by
   * scrollable/overflow-hidden ancestors, and fires on carousel drags too, not just page scroll. A MutationObserver
   * picks up cards added later, e.g. via "load more"/infinite scroll or a carousel rendering more slides.
   */
  var mobileQuery = window.matchMedia('(max-width: 699px)');
  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var timers = new WeakMap();

  function stopSecondaryVideo(figure) {
    var timer = timers.get(figure);
    if (timer) {
      clearTimeout(timer);
      timers.delete(figure);
    }

    var video = figure.querySelector('video-media.product-card__image--secondary');
    if (video && typeof video.pause === 'function') {
      video.pause();
    }
    figure.classList.remove('product-card__figure--video-active');
  }

  function scheduleSecondaryVideo(figure) {
    var timer = setTimeout(function () {
      timers.delete(figure);

      if (!mobileQuery.matches || reducedMotionQuery.matches) return;

      var video = figure.querySelector('video-media.product-card__image--secondary');
      if (video && typeof video.play === 'function') {
        figure.classList.add('product-card__figure--video-active');
        playSecondaryVideo(video);
      }
    }, 3000);

    timers.set(figure, timer);
  }

  var visibilityObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var figure = entry.target;

      if (entry.isIntersecting) {
        if (!timers.has(figure)) {
          scheduleSecondaryVideo(figure);
        }
      } else {
        stopSecondaryVideo(figure);
      }
    });
  }, { threshold: 0.5 });

  function observeFigure(figure) {
    if (figure.querySelector('video-media.product-card__image--secondary')) {
      visibilityObserver.observe(figure);
    }
  }

  document.querySelectorAll('.product-card__figure').forEach(observeFigure);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;

        if (node.matches && node.matches('.product-card__figure')) {
          observeFigure(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.product-card__figure').forEach(observeFigure);
        }
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
